import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, getToken, setToken, decodeTokenRole } from '../api/client.js';

export function useAuth() {
  const [token, setLocalToken] = useState(() => getToken());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'stories:token') setLocalToken(e.newValue);
    };
    const onTokenChanged = () => setLocalToken(getToken());
    window.addEventListener('storage', onStorage);
    window.addEventListener('stories:token-changed', onTokenChanged);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('stories:token-changed', onTokenChanged);
    };
  }, []);

  const role = useMemo(() => decodeTokenRole(token), [token]);

  const login = useCallback(async (password, requestedRole = 'user') => {
    setBusy(true);
    setError(null);
    try {
      const { token: t } = await api.auth.check(password, requestedRole);
      setToken(t);
      setLocalToken(t);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const redeemInvite = useCallback(async (code) => {
    setBusy(true);
    setError(null);
    try {
      const { token: t } = await api.auth.redeemInvite(code);
      setToken(t);
      setLocalToken(t);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setLocalToken(null);
  }, []);

  return {
    token,
    role,
    isAuthed: !!token,
    isAdmin: role === 'admin',
    isInvite: role === 'invite',
    login,
    redeemInvite,
    logout,
    busy,
    error,
  };
}
